import {
  Component,
  ViewChild,
  ElementRef,
  OnInit,
  EventEmitter,
  Output,
  Input,
  SimpleChanges,
} from '@angular/core';
import { fabric } from 'fabric';
import { MatDialog } from '@angular/material/dialog';
import { CreativePreviewComponent } from '../creative-preview/creative-preview.component';
import { ToastrService } from 'ngx-toastr';
import { MatMenuTrigger } from '@angular/material/menu';
import { DcoLibraryService } from 'src/app/core/services/dco/dco-library.service';
import { firstValueFrom } from 'rxjs';
import { parseGIF, decompressFrames } from "gifuct-js";
import { FontLoaderService } from 'src/app/core/services/dco/dco-font-loader.service';

@Component({
  selector: "app-creative-editor",
  templateUrl: "./creative-editor.component.html",
  styleUrl: "./creative-editor.component.scss",
})
export class CreativeEditorComponent implements OnInit {
  @Input() stepInput: any;
  @ViewChild('fontInput') fontInput!: ElementRef;
  @Output() step_change = new EventEmitter<any>();
  @ViewChild("sizeTrigger") sizeTrigger!: MatMenuTrigger;
  @ViewChild("canvasRef", { static: true })
  canvasRef!: ElementRef<HTMLCanvasElement>;
  canvas!: fabric.Canvas;
  searchText: any;

  constructor(
    public dialog: MatDialog,
    private toastr: ToastrService,
    private dcoLibraryService: DcoLibraryService,
    private fontLoader: FontLoaderService
  ) {}

  creativeWidth: number = 0;
  creativeHeight: number = 0;
  feedColumns: Array<any> = [];
  clicksFromFeed: Array<any> = [];
  selectedFont: string = "";
  sizeSearchText: string = "";
  placeholderSearchText: string = "";
  fonts: any[] = [
    { label: "Arial", value: "Arial" },
    { label: "Roboto", value: "Roboto" },
    { label: "Verdana", value: "Verdana" },
    { label: "Helvetica", value: "Helvetica" },
    { label: "Times New Roman", value: "Times New Roman" },
    { label: "Courier New", value: "Courier New" },
    { label: "Georgia", value: "Georgia" },
    { label: "Palatino", value: "Palatino" },
    { label: "Garamond", value: "Garamond" },
    { label: "Bookman", value: "Bookman" },
    { label: "Comic Sans MS", value: "Comic Sans MS" },
    { label: "Impact", value: "Impact" },
    { label: "Lucida Console", value: "Lucida Console" },
    { label: "Lucida Sans Unicode", value: "Lucida Sans Unicode" },
    { label: "Tahoma", value: "Tahoma" },
    { label: "Trebuchet MS", value: "Trebuchet MS" },
    { label: "Webdings", value: "Webdings" },
    { label: "Wingdings", value: "Wingdings" },
    { label: "Symbol", value: "Symbol" },
    { label: "Arial Black", value: "Arial Black" },
    { label: "Arial Narrow", value: "Arial Narrow" },
    { label: "Arial Rounded MT Bold", value: "Arial Rounded MT Bold" },
    { label: "Arial Unicode MS", value: "Arial Unicode MS" },
  ];
  fontAssets: any[] = [];
  selectedObject: any = null;
  currentFontSize = 16;
  shapeColor: string = "";
  selectedRadius: any;
  selectedShape: string = "";
  undoStack: string[] = [];
  redoStack: string[] = [];
  feedColumnsObj: any = {};
  allLibElements: Array<any> = [];
  isRestoring = false;
  allTemplates: Array<any> = [];
  allFonts: Array<any> = [];
  templateId: string = '';
  isEdit: boolean = false;
  isCanvasRestoring = false;
  creativeSizes = [
    { label: "250×250", value: "250x250" },
    { label: "300×90", value: "300x90" },
    { label: "300×250", value: "300x250" },
    { label: "300×300", value: "300x300" },
    { label: "300×600", value: "300x600" },
    { label: "320×50", value: "320x50" },
    { label: "320×100", value: "320x100" },
    { label: "320×480", value: "320x480" },
    { label: "480×320", value: "480x320" },
    { label: "728×90", value: "728x90" },
    { label: "970×90", value: "970x90" },
    { label: "970×250", value: "970x250" },
    { label: "980×240", value: "980x240" },
  ];
  currentAlign: "left" | "center" | "right" = "left";
  customClickUrl: string = "";
  selectedAssetType: any;
  isUnderline: boolean = false;
  isItalic: boolean = false;
  isBold: boolean = false;
  templateSelected: boolean = false;

  ngOnInit() {
    this.initializeCanvas();
    this.listenForPaste();
    this.bindCanvasChangeEvents();
    this.canvas.on("object:added", () => this.saveState());
    this.canvas.on("object:modified", () => this.saveState());
    this.canvas.on("object:removed", () => this.saveState());
    this.extendFabricToObject();

    this.fontLoader.getFontAssets(this.stepInput?.globalParams).then((assets) => {
      this.mergeFontAssets(assets);
    });

    if (this.stepInput?.isEdit) {
      if (this.stepInput?.component === "dcoLibrary") {
        const width = this.stepInput.width;
        const height = this.stepInput.height;
        setTimeout(() => {
          this.loadCanvasFromJSON(this.stepInput?.meta, width, height);
        });
      } else {
        const template = this.templateId
          ? this.allTemplates.find((t) => t.id === this.stepInput.templateId)
          : null;

        if (template) {
          setTimeout(() => {
            this.loadCanvasFromJSON(
              { objects: this.stepInput?.meta.objects.filter((e: any) => !e.remove_backend) },
              template.width,
              template.height
            );
          });
        } else {
          setTimeout(() => {
            this.loadCanvasFromJSON(
              { objects: this.stepInput?.meta.objects.filter((e: any) => !e.remove_backend) },
              this.stepInput?.width,
              this.stepInput?.height
            );
          });
        }
      }
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['stepInput']) {
      this.setFeedColumns(changes['stepInput'].currentValue?.feedColumns);
      this.allLibElements = changes['stepInput'].currentValue?.allLibElements;
      this.isEdit = changes['stepInput'].currentValue?.isEdit;
      this.allTemplates = changes['stepInput'].currentValue?.allTemplates;
      const prevFonts = changes['stepInput'].previousValue?.allFonts;
      const currFonts = changes['stepInput'].currentValue?.allFonts;
      if (currFonts && currFonts !== prevFonts) {
        this.mergeFontAssets(currFonts);
      }
      if (this.stepInput?.component !== "dcoLibrary") {
        this.templateId = changes['stepInput'].currentValue?.templateId;
      }
    }
  }

  private mergeFontAssets(assets: any[]) {
    if (!assets?.length) return;

    const knownAssets = new Set(this.fontAssets.map((a) => a.name));
    const newAssets = assets.filter((a) => a?.name && !knownAssets.has(a.name));
    if (newAssets.length) {
      this.fontAssets = [...this.fontAssets, ...newAssets];
    }

    const knownOptions = new Set(this.fonts.map((f) => f.value));
    const newOptions = assets
      .filter((a) => a?.name && !knownOptions.has(a.name))
      .map((a) => ({ label: a.name, value: a.name }));
    if (!newOptions.length) return;
    this.fonts = [...this.fonts, ...newOptions];

    const active = this.canvas?.getActiveObject() as any;
    if (active?.type === 'textbox') this.selectedFont = this.getDropdownValueForText(active);
  }

  trackFont = (_: number, f: any) => f.value;

  private getDropdownValueForText(obj: any): string {
    const weight = this.fontLoader.normalizeWeight(obj.fontWeight);
    const style = obj.fontStyle === 'italic' || obj.fontStyle === 'oblique' ? 'italic' : 'normal';
    const family = (obj.fontFamily || '').toLowerCase();

    const exact = this.fontAssets.find((a) => {
      const d = this.fontLoader.toDescriptor(a);
      return d.family.toLowerCase() === family && d.weight === weight && d.style === style;
    });
    if (exact) return exact.name;

    const sameFamily = this.fontAssets.find(
      (a) => this.fontLoader.toDescriptor(a).family.toLowerCase() === family
    );
    if (sameFamily) return sameFamily.name;

    if (this.fonts.some((f) => f.value === obj.fontFamily)) return obj.fontFamily;

    if (obj.fontFamily) {
      this.fonts = [...this.fonts, { label: obj.fontFamily, value: obj.fontFamily }];
      return obj.fontFamily;
    }
    return 'Arial';
  }

  ngAfterViewInit() {
    this.canvas.on("selection:created", (e) => {
      this.selectedObject = e.selected![0];
      this.onObjectSelected(e);
    });
    this.canvas.on("selection:updated", (e) => {
      this.selectedObject = e.selected![0];
      this.onObjectSelected(e);
    });
    this.canvas.on("selection:cleared", () => {
      this.selectedObject = null;
    });
    this.initUndoRedo();
  }

listenForPaste() {
    document.addEventListener('paste', async (event: ClipboardEvent) => {
      const target = event.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return;
      }
      const activeObj = this.canvas?.getActiveObject();
      if (activeObj && (activeObj as any).isEditing) {
        return;
      }
      const items = event.clipboardData?.items;
      if (!items) return;
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type.indexOf('image') !== -1) {
          const blob = item.getAsFile();
          if (!blob) continue;
          const reader = new FileReader();
          reader.onload = (e: any) => {
            fabric.Image.fromURL(e.target.result, (img) => {
              if (this.creativeWidth && this.creativeHeight) {
                const formData = new FormData();
                const sanitizedBlobName = decodeURIComponent(blob.name).replace(/\s+/g, '_');
                const renamedFile = new File([blob], sanitizedBlobName, { type: blob.type });
                formData.append("file", renamedFile);
                formData.append("asset_name", sanitizedBlobName);
                formData.append("name", sanitizedBlobName.split(".")[0] + '_' + Date.now());
                formData.append("description", "");
                formData.append("asset_type", "image");
                formData.append("width", "100");
                formData.append("height", "100");
                formData.append("meta", JSON.stringify({}));
                formData.append("hidden", "true");
 
                this.dcoLibraryService.create(formData, this.stepInput.globalParams).subscribe((res) => {
                  this.uploadImageFromUrl(
                    {
                      asset_url: res.url,
                    },
                    false,
                    false
                  );
                });
              }
            });
          };
 
          reader.readAsDataURL(blob);
        }
      }
      const text = event.clipboardData?.getData('text');
      if (text) {
        const textObj = new fabric.Textbox(text, {
          left: 100,
          top: 100,
          fontSize: 24,
          fill: '#000'
        });
 
        this.canvas.add(textObj);
        this.canvas.setActiveObject(textObj);
        this.canvas.renderAll();
      }
    });
  }

  onObjectSelected(e: any) {
    const obj = e.selected?.[0];
    if (!obj) return;
    this.selectedObject = obj;

    if (obj.type === "textbox") {
      this.currentFontSize = obj.fontSize || 16;
      this.currentAlign = obj.textAlign || "left";
      this.selectedFont = this.getDropdownValueForText(obj);
      this.shapeColor = obj.fill?.toString() || "#000000";
      this.isBold = obj.fontWeight === "bold";
      this.isItalic = obj.fontStyle === "italic";
      this.isUnderline = obj.underline === true;
    }
    if (obj.type == "rect") {
      const rect = obj as fabric.Rect;
      this.selectedRadius = rect.rx || rect.ry || 0;
      this.shapeColor = rect.fill?.toString() || "#4caf50";
    }
    if (obj.type == "circle") {
      this.shapeColor = (obj as fabric.Circle).fill?.toString() || "#4caf50";
    }
    if (obj.type == "triangle") {
      this.shapeColor = (obj as fabric.Triangle).fill?.toString() || "#4caf50";
    }
    if (obj.type == "image") {
      const img = obj as fabric.Image;
      const selectedTemplate = this.filteredLibElements.filter(
        (lib) => lib.asset_url == img._element?.src
      );
      this.selectedAssetType = selectedTemplate[0]?.["asset_type"];
      if (this.selectedAssetType == "button") {
        this.customClickUrl = (img as any).obj_click_url;
      }
    }
  }

  loadTemplate() {
    if (this.isEdit && this.stepInput.meta) {
      setTimeout(() => {
        this.loadCanvasFromJSON(this.stepInput.meta, 300, 300);
      });
    }
  }

  initUndoRedo() {
    const saveState = () => {
      if (this.isRestoring) return;

      this.undoStack.push(JSON.stringify(this.getSafeJSON()));
      this.redoStack = [];
    };
    this.canvas.on("object:added", saveState);
    this.canvas.on("object:modified", saveState);
    this.canvas.on("object:removed", saveState);
  }

  extendFabricToObject() {
    fabric.Object.prototype.toObject = (function (toObject) {
      return function (this: any) {
        return fabric.util.object.extend(toObject.call(this), {
          backend_value_src: this.backend_value_src,
          backend_value_text: this.backend_value_text,
          remove_backend: this.remove_backend,
          text: this.text || "",
          obj_click_url: this.obj_click_url,
          font_url: this.font_url,
          _gifUrl: this._gifUrl,
          isAnimatedGif: this.isAnimatedGif,
        });
      };
    })(fabric.Object.prototype.toObject);
  }

  setFeedColumns(data: any) {
    this.feedColumnsObj = data;
    this.feedColumns = Object.entries(data)
      .map(([key, value]) => ({ name: key.replace(/_/g, " "), key, type: value }))
      .filter((item: any) => item.type != "click_url");
    this.clicksFromFeed = Object.entries(data)
      .map(([key, value]) => ({ name: key.replace(/_/g, " "), key, type: value }))
      .filter((item: any) => item.type == "click_url");
  }

  addPlaceholder(item: any) {
    if (item.type === "image") {
      this.uploadImageFromUrl(item, true);
    } else {
      this.addText(false, item);
    }
  }

  configureClickUrl(item: any) {
    const obj = this.canvas.getActiveObject();
    if (obj?.type === "image") {
      this.addClickUrl(item, true);
    }
  }

  setCustomClickUrl() {
  if (!this.customClickUrl) return;
  if (!/^https?:\/\//i.test(this.customClickUrl)) {
    this.toastr.error("Please enter a valid URL starting with http or https");
    return;
  }
  const obj = this.canvas.getActiveObject();
  if (!obj) {
    this.toastr.error("No object selected");
    return;
  }
  
  obj.set("obj_click_url", this.customClickUrl);
  this.canvas.renderAll();
  this.toastr.success("Click URL applied");
  this.customClickUrl = "";
}

  bindCanvasChangeEvents() {
    const triggerUpdate = () => {
      if (this.isCanvasRestoring) return;
      this.updateChanges(false);
    };
    this.canvas.on("object:added", triggerUpdate);
    this.canvas.on("object:removed", triggerUpdate);
    this.canvas.on("object:modified", triggerUpdate);
    this.canvas.on("object:moving", triggerUpdate);
    this.canvas.on("object:scaling", triggerUpdate);
    this.canvas.on("object:rotating", triggerUpdate);
    this.canvas.on("object:skewing", triggerUpdate);
    this.canvas.on("text:changed", triggerUpdate);
    this.canvas.on("text:editing:entered", triggerUpdate);
    this.canvas.on("text:editing:exited", triggerUpdate);
    this.canvas.on("selection:created", triggerUpdate);
    this.canvas.on("selection:updated", triggerUpdate);
    this.canvas.on("selection:cleared", triggerUpdate);
    this.canvas.on("after:render", () => {});
  }

  get selectedWidth(): number {
    return this.selectedObject ? this.selectedObject.width! * this.selectedObject.scaleX! : 0;
  }

  get selectedHeight(): number {
    return this.selectedObject ? this.selectedObject.height! * this.selectedObject.scaleY! : 0;
  }

  get dynamicContainerHeight(): number {
    if (!this.creativeWidth || !this.creativeHeight || typeof window === "undefined") return 100;
    const windowHeight = window.innerHeight;
    const windowWidth = window.innerWidth;

    const canvasAspectRatio = this.creativeHeight / this.creativeWidth;

    const maxCanvasWidth = windowWidth * 0.9;
    const maxCanvasHeight = windowHeight * 0.8;

    const heightFromWidth = maxCanvasWidth * canvasAspectRatio;

    const finalCanvasHeight = Math.min(heightFromWidth, maxCanvasHeight);

    const toolbarHeight = 100;
    const totalHeight = finalCanvasHeight + toolbarHeight;
    return (totalHeight / windowHeight) * 100;
  }

  saveState() {
    if (this.isRestoring) return;
    this.undoStack.push(JSON.stringify(this.getSafeJSON()));
    this.redoStack = [];
  }

  undo() {
    if (this.undoStack.length <= 1 || !this.canvas) return;
    this.isRestoring = true;
    this.redoStack.push(JSON.stringify(this.getSafeJSON()));
    this.undoStack.pop();
    const stateStr = this.undoStack[this.undoStack.length - 1];
    this.restoreCanvasState(stateStr);
  }

  redo() {
    if (!this.redoStack.length || !this.canvas) return;
    this.isRestoring = true;
    this.undoStack.push(JSON.stringify(this.getSafeJSON()));
    const stateStr = this.redoStack.pop()!;
    this.restoreCanvasState(stateStr);
  }

  private restoreCanvasState(stateStr: string) {
    try {
      const state = JSON.parse(stateStr);
      const width = state.width ?? this.creativeWidth;
      const height = state.height ?? this.creativeHeight;
      this.creativeWidth = width;
      this.creativeHeight = height;
      this.canvas.setWidth(width);
      this.canvas.setHeight(height);
      this.canvas.loadFromJSON(state, () => {
        this.canvas.renderAll();
        this.restrictMovementInsideCanvas();
        this.canvas.getObjects('image').forEach((el: fabric.Object) => {
          if ((el as any).backend_value_src) this.drawPlaceholderLabel(el as fabric.Image);
        });
        this.isRestoring = false;
        this.updateChanges(false);
      });
    } catch {
      this.isRestoring = false;
    }
  }

  initializeCanvas() {
    this.canvas = new fabric.Canvas(this.canvasRef.nativeElement, {
      preserveObjectStacking: true,
      backgroundColor: "#ffffff",
    });
    this.canvas.setWidth(this.creativeWidth);
    this.canvas.setHeight(this.creativeHeight);
    this.restrictMovementInsideCanvas();
    this.enableDeleteKey();
    this.undoStack.push(JSON.stringify(this.getSafeJSON()));
  }

  setTextAlign(align: "left" | "center" | "right") {
    const obj = this.canvas.getActiveObject();
    if (!obj || obj.type !== "textbox") return;
    (obj as fabric.Textbox).set("textAlign", align);
    this.currentAlign = align;
    this.canvas.renderAll();
  }

  async applyFont(value: string) {
    if (!value) return;

    if (value === '__custom__') {
      this.selectedFont = null;
      this.fontInput.nativeElement.click();
      return;
    }

    const obj = this.canvas.getActiveObject() as fabric.Textbox;
    if (!obj || obj.type !== 'textbox') return;

    const asset = this.fontAssets.find((f) => f.name === value);

    if (asset) {
      const desc = this.fontLoader.toDescriptor(asset);
      await this.fontLoader.loadFont(desc);
      obj.set({
        fontFamily: desc.family,
        fontWeight: desc.weight,
        fontStyle: desc.style,
        font_url: desc.url,
      } as any);
      this.isBold = false;
      this.isItalic = desc.style === 'italic';
    } else {
      obj.set({ fontFamily: value, font_url: null } as any);
    }

    obj.initDimensions();
    obj.setCoords();
    this.canvas.requestRenderAll();
    this.selectedFont = value;
    this.canvas.fire('object:modified', { target: obj });
  }


  changeCreativeSize(event: any) {
    const [w, h] = event.value.split("x").map(Number);
    this.creativeWidth = w;
    this.creativeHeight = h;
    if (this.canvas) {
      this.canvas.setWidth(w);
      this.canvas.setHeight(h);
      this.canvas.clear();
      this.canvas.renderAll();
    }
  }

  changeTemplate(template: any) {
    this.templateId = template.id;
    this.templateSelected = true;
    if (template.id != 'new_template') {
      this.canvas.clear();
      this.loadCanvasFromJSON(template.meta, template.width, template.height);
    }
    if (template.id == "new_template") {
      setTimeout(() => this.sizeTrigger?.openMenu());
      this.changeCreativeSize({ value: "250x250" });
    }
  }

  deleteObject() {
    const obj = this.canvas.getActiveObject();
    if (obj) {
      this.canvas.remove(obj);
      this.canvas.renderAll();
    }
  }

  enableDeleteKey() {
    document.addEventListener("keydown", (event) => {
      if (event.key === "Delete") this.deleteObject();
    });
  }

  bringToFront() {
    const obj = this.canvas.getActiveObject();
    if (obj) { obj.bringToFront(); this.canvas.renderAll(); }
  }

  sendToBack() {
    const obj = this.canvas.getActiveObject();
    if (obj) { obj.sendToBack(); this.canvas.renderAll(); }
  }

  get filteredPlaceholders() {
    if (!this.placeholderSearchText) {
      return this.feedColumns;
    }

    const value = this.placeholderSearchText.toLowerCase();

    return this.feedColumns.filter(item =>
      item.name?.toLowerCase().includes(value) ||
      item.type?.toLowerCase().includes(value)
    );
  }

  get filteredCreativeSizes() {
    if (!this.sizeSearchText) {
      return this.creativeSizes;
    }

    const value = this.sizeSearchText.toLowerCase();
    return this.creativeSizes.filter(size =>
      size.label.toLowerCase().includes(value) ||
      size.value?.toString().toLowerCase().includes(value)
    );
  }


  get filteredLibElements() {
    if (!this.searchText) {
      return this.allLibElements;
    }

    const value = this.searchText.toLowerCase();
    return this.allLibElements.filter(item =>
      item.name.toLowerCase().includes(value) ||
      item.asset_type.toLowerCase().includes(value)
    );
  }
  addClickUrl(item: any, from_feed: boolean = false) {
    const obj = this.canvas.getActiveObject();
    if (obj) {
      if (from_feed) {
        obj.set("backend_value_obj_click_url", item.key);
        obj.set("obj_click_url", null);
      } else {
        obj.set("obj_click_url", item.key);
      }
      this.canvas.renderAll();
    }
  }

  addText(is_edit: boolean = true, item: any = null) {
    const displayText = is_edit ? "Your Text" : `%%${item?.name}%%`;
    const text = new fabric.Textbox(displayText, {
      left: 20, top: 20, fontSize: 20, fill: "#000", fontFamily: "Arial", editable: is_edit,
    });
    if (!is_edit && item?.key) {
      (text as any).backend_value_text = `%%${item.key}%%`;
    }
    this.canvas.add(text);
    this.canvas.renderAll();
  }

  changeColor(e: any) {
    const obj = this.canvas.getActiveObject();
    if (obj && obj.type === "textbox") {
      obj.set("fill", e.target.value);
      this.canvas.renderAll();
    }
  }

  adjustFontSize(action: "increase" | "decrease") {
    const obj = this.canvas.getActiveObject();
    if (!obj || obj.type !== "textbox") return;
    const current = (obj as fabric.Textbox).fontSize || 16;
    let newSize = action === "increase" ? current + 1 : current - 1;
    newSize = Math.min(150, Math.max(10, newSize));
    (obj as fabric.Textbox).set("fontSize", newSize);
    this.currentFontSize = newSize;
    this.canvas.renderAll();
  }

  changeFontSize(event: any) {
    const obj = this.canvas.getActiveObject();
    if (!obj || obj.type !== "textbox") return;
    (obj as fabric.Textbox).set("fontSize", Number(event));
    this.currentFontSize = Number(event);
    this.canvas.renderAll();
  }

  toggleBold() {
    const obj = this.canvas.getActiveObject();
    if (!obj || obj.type !== "textbox") return;
    obj.set("fontWeight", obj.fontWeight === "bold" ? "normal" : "bold");
    obj.setCoords();
    this.canvas.requestRenderAll();
  }

  toggleItalic() {
    const obj = this.canvas.getActiveObject();
    if (!obj || obj.type !== "textbox") return;
    obj.set("fontStyle", obj.fontStyle === "italic" ? "normal" : "italic");
    obj.setCoords();
    this.canvas.requestRenderAll();
  }

  toggleUnderline() {
    const obj = this.canvas.getActiveObject();
    if (!obj || obj.type !== "textbox") return;
    obj.set("underline", !obj.underline);
    obj.setCoords();
    this.canvas.requestRenderAll();
  }

  uploadImage(event: any) {
    const file = event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      fabric.Image.fromURL(reader.result as string, (img) => {
        img.scaleToWidth(150);
        img.set({ left: 20, top: 20 });
        this.canvas.add(img);
      });
    };
    reader.readAsDataURL(file);
  }

  private buildGifRenderLoop(frames: any[], width: number, height: number, fabricTarget: any) {
    const compositeCanvas = document.createElement("canvas");
    compositeCanvas.width = width;
    compositeCanvas.height = height;
    const compositeCtx = compositeCanvas.getContext("2d")!;

    const frameCanvas = document.createElement("canvas");
    frameCanvas.width = width;
    frameCanvas.height = height;
    const frameCtx = frameCanvas.getContext("2d")!;

    const patchCanvas = document.createElement("canvas");
    const patchCtx = patchCanvas.getContext("2d")!;

    fabricTarget.setElement(frameCanvas);
    fabricTarget.dirty = true;

    let frameIndex = 0;

    const renderFrame = () => {
      if (!this.canvas.getObjects().includes(fabricTarget)) return;

      const frame = frames[frameIndex];

      patchCanvas.width = frame.dims.width;
      patchCanvas.height = frame.dims.height;

      const patchData = new ImageData(
        new Uint8ClampedArray(frame.patch),
        frame.dims.width,
        frame.dims.height
      );
      patchCtx.putImageData(patchData, 0, 0);

      if (frame.disposalType === 2) {
        compositeCtx.clearRect(0, 0, width, height);
      }

      compositeCtx.drawImage(patchCanvas, frame.dims.left, frame.dims.top);

      frameCtx.clearRect(0, 0, width, height);
      frameCtx.drawImage(compositeCanvas, 0, 0);

      fabricTarget.setElement(frameCanvas);
      fabricTarget.dirty = true;
      this.canvas.renderAll();

      frameIndex = (frameIndex + 1) % frames.length;
      setTimeout(renderFrame, frame.delay || 100);
    };

    renderFrame();
  }

  async addAnimatedGif(
    url: string,
    item?: any,
    is_default: boolean = false,
    fit_to_canvas: boolean = false
  ) {
    const response = await fetch(url);
    const buffer = await response.arrayBuffer();
    const gif = parseGIF(buffer);
    const frames = decompressFrames(gif, true);
    if (!frames.length) return;

    const width = frames[0].dims.width;
    const height = frames[0].dims.height;

    const fabricImg = new fabric.Image(document.createElement("canvas"), {
      left: 20,
      top: 20,
      selectable: true,
    });

    (fabricImg as any)._gifUrl = url;
    (fabricImg as any).isAnimatedGif = true;

    const canvasWidth = this.creativeWidth || this.canvas.getWidth();
    const canvasHeight = this.creativeHeight || this.canvas.getHeight();

    if (fit_to_canvas) {
      const scale = Math.min(canvasWidth / width, canvasHeight / height);

      const scaledWidth = width * scale;
      const scaledHeight = height * scale;

      fabricImg.set({
        scaleX: scale,
        scaleY: scale,
        left: (canvasWidth - scaledWidth) / 2,
        top: (canvasHeight - scaledHeight) / 2,
        originX: "left",
        originY: "top",
      });
    } else {
        const scale = 150 / width;
        fabricImg.set({
          scaleX: scale,
          scaleY: scale,
          left: 20,
          top: 20,
          originX: 'left',
          originY: 'top',
        });
    }

    if (is_default && item) {
      (fabricImg as any).backend_value_src = `%%${item.key}%%`;
      (fabricImg as any).isPlaceholder = true;
      (fabricImg as any).pf_name = item.key;
    }

    if (item?.asset_type === "button") {
      fabricImg.set({ obj_click_url: item.click_url } as any);
    }

    this.canvas.add(fabricImg);
    this.canvas.setActiveObject(fabricImg);
    this.canvas.renderAll();

    if (item) this.selectedAssetType = item.asset_type;
    if (is_default && item) this.drawPlaceholderLabel(fabricImg);

    this.buildGifRenderLoop(frames, width, height, fabricImg);
  }

  async reAnimateGif(fabricObj: any, gifUrl: string): Promise<void> {
    try {
      const response = await fetch(gifUrl);
      const buffer = await response.arrayBuffer();
      const gif = parseGIF(buffer);
      const frames = decompressFrames(gif, true);
      if (!frames.length) return;

      const width = frames[0].dims.width;
      const height = frames[0].dims.height;

      fabricObj._gifUrl = gifUrl;
      fabricObj.isAnimatedGif = true;

      this.buildGifRenderLoop(frames, width, height, fabricObj);
    } catch (err) {
      console.error("[GIF] Failed to re-animate:", gifUrl, err);
    }
  }

  uploadImageFromUrl(item: any, is_default: boolean = false, fit_to_canvas: boolean = false) {
    const pf_name = item.key;
    const url = is_default
      ? "https://assets-prod-1234.s3.us-east-1.amazonaws.com/shared/creatives/placeholder.png"
      : item?.asset_url;

    if (url?.toLowerCase().includes(".gif")) {
      this.addAnimatedGif(url, item, is_default, fit_to_canvas);
      return;
    }

    fabric.Image.fromURL(
      url,
      (img) => {
        const canvasWidth = this.creativeWidth || this.canvas.getWidth();
        const canvasHeight = this.creativeHeight || this.canvas.getHeight();
        if (fit_to_canvas) {
          const canvasWidth = this.canvas.getWidth();
          const canvasHeight = this.canvas.getHeight();

          const imgWidth = img.width || 1;
          const imgHeight = img.height || 1;

        if (fit_to_canvas) {
          const scale = Math.min(canvasWidth / (img.width || 1), canvasHeight / (img.height || 1));
          img.scale(scale);
          img.set({
            left: (canvasWidth - img.getScaledWidth()) / 2,
            top: (canvasHeight - img.getScaledHeight()) / 2,
            selectable: true,
          });
        } else {
          img.scaleToWidth(150);
          img.set({ left: 20, top: 20, selectable: true });
        }

          const scale = Math.min(
            canvasWidth / imgWidth,
            canvasHeight / imgHeight
          );

          img.scale(scale);

          img.set({
            left: (canvasWidth - img.getScaledWidth()) / 2,
            top: (canvasHeight - img.getScaledHeight()) / 2,
            selectable: true
          });
        } else {
          img.scaleToWidth(150);
          img.set({
            left: 20,
            top: 20,
            selectable: true
          });
        }
        if (is_default) {
          (img as any).backend_value_src = `%%${pf_name}%%`;
          (img as any).isPlaceholder = true;
          (img as any).pf_name = pf_name;
        }

        if (item.asset_type === "button") {
          (img as any).obj_click_url = item.click_url;
        }

        this.canvas.add(img);
        this.canvas.setActiveObject(img);
        this.canvas.renderAll();
        this.selectedAssetType = item.asset_type;

        if (is_default) this.drawPlaceholderLabel(img);
      }
    );
  }

  drawPlaceholderLabel(img: fabric.Image) {
    const BASE_FONT_SIZE = 100;
    const plName = (img as any).backend_value_src
      ? (img as any).backend_value_src.replaceAll("%%", "")
      : (img as any).pf_name;

    const label = new fabric.Text(plName, {
      fontSize: BASE_FONT_SIZE,
      fill: "#000000",
      selectable: false,
      evented: false,
      originX: "center",
      originY: "center",
    });
    (label as any).remove_backend = true;

    const updatePositionAndScale = () => {
      const scale = Math.min(img.scaleX || 1, img.scaleY || 1);
      label.set({
        left: img.left! + img.getScaledWidth() / 2,
        top: img.top! + img.getScaledHeight() / 2,
        scaleX: scale,
        scaleY: scale,
      });
    };

    updatePositionAndScale();
    this.canvas.add(label);
    img.on("moving", updatePositionAndScale);
    img.on("scaling", updatePositionAndScale);
    img.on("rotating", updatePositionAndScale);
    img.on("removed", () => this.canvas.remove(label));
  }

  zoomIn() { this.canvas.setZoom(this.canvas.getZoom() + 0.1); }
  zoomOut() { this.canvas.setZoom(this.canvas.getZoom() - 0.1); }
  exportJSON() { console.log("JSON:", this.getSafeJSON()); }

  restrictMovementInsideCanvas() {
    this.canvas.on("object:moving", (e) => {
      const obj = e.target!;
      obj.setCoords();
      const bounds = obj.getBoundingRect(true);
      const maxW = this.canvas.width!;
      const maxH = this.canvas.height!;
      if (bounds.left < 0) obj.left = 0;
      if (bounds.top < 0) obj.top = 0;
      if (bounds.left + bounds.width > maxW) obj.left = maxW - bounds.width;
      if (bounds.top + bounds.height > maxH) obj.top = maxH - bounds.height;
      this.canvas.renderAll();
    });
  }

  selectObject(obj: any) { this.selectedObject = obj; }

  changeButtonFontFamily(font: string) {
    const parts = this.getButtonParts();
    if (!parts) return;
    parts.text.set("fontFamily", font);
    this.canvas.renderAll();
  }

  changeFontWeight(e: any) {
    const obj = this.canvas.getActiveObject();
    if (obj && obj.type === "textbox") {
      (obj as fabric.Textbox).set("fontWeight", e.target.value);
      this.canvas.renderAll();
    }
  }

  changeTextBackground(e: any) {
    const obj = this.canvas.getActiveObject();
    if (obj && obj.type === "textbox") {
      (obj as fabric.Textbox).set("textBackgroundColor", e.target.value);
      this.canvas.renderAll();
    }
  }

  changeTextAlign(e: any) {
    const obj = this.canvas.getActiveObject();
    if (obj && obj.type === "textbox") {
      (obj as fabric.Textbox).set("textAlign", e.target.value);
      this.canvas.renderAll();
    }
  }

  addButton() {
    const rect = new fabric.Rect({ width: 150, height: 45, fill: "#007bff", rx: 8, ry: 8 });
    const text = new fabric.Textbox("Button", { fontSize: 18, fill: "#fff", textAlign: "center", fontWeight: "600", width: 150 });
    const group = new fabric.Group([rect, text], { left: 50, top: 50 });
    (group as any).customType = "button";
    this.canvas.add(group);
    this.canvas.setActiveObject(group);
  }

  addRectangle() {
    this.canvas.add(new fabric.Rect({ width: 150, height: 100, fill: "#4caf50", rx: 0, ry: 0 }));
  }

  addCircle() {
    this.canvas.add(new fabric.Circle({ radius: 60, fill: "#ff5722" }));
  }

  addTriangle() {
    this.canvas.add(new fabric.Triangle({ width: 120, height: 100, fill: "#9c27b0" }));
  }

  addLine() {
    this.canvas.add(new fabric.Line([0, 0, 150, 0], { stroke: "#000", strokeWidth: 3 }));
  }

  private getButtonParts() {
    const obj = this.canvas.getActiveObject();
    if (!obj || (obj as any).customType !== "button") return null;
    const group = obj as fabric.Group;
    const objects = group.getObjects();
    return { rect: objects[0] as fabric.Rect, text: objects[1] as fabric.Textbox, group };
  }

  changeButtonFontSize(event: any) {
    const parts = this.getButtonParts();
    if (!parts) return;
    parts.text.set("fontSize", Number(event.target.value));
    this.canvas.renderAll();
  }

  changeButtonBg(event: any) {
    const parts = this.getButtonParts();
    if (!parts) return;
    parts.rect.set("fill", event.target.value);
    this.canvas.renderAll();
  }

  changeButtonText(value: string) {
    const parts = this.getButtonParts();
    if (!parts) return;
    parts.text.set("text", value);
    parts.text.setCoords();
    this.canvas.renderAll();
  }

  changeButtonTextColor(event: any) {
    const parts = this.getButtonParts();
    if (!parts) return;
    parts.text.set("fill", event.target.value);
    this.canvas.renderAll();
  }

  changeButtonFontWeight(event: any) {
    const parts = this.getButtonParts();
    if (!parts) return;
    parts.text.set("fontWeight", event.target.value);
    this.canvas.renderAll();
  }

  changeButtonRadius(event: any) {
    const parts = this.getButtonParts();
    if (!parts) return;
    const radius = Number(event.target.value);
    parts.rect.set({ rx: radius, ry: radius });
    this.canvas.renderAll();
  }

  changeShapeRadius(event: any) {
    const active = this.canvas.getActiveObject();
    if (!active) return;
    const radius = Number(event.target.value);
    if (active.type === "rect") {
      (active as fabric.Rect).set({ rx: radius, ry: radius });
      active.setCoords();
      this.canvas.renderAll();
      return;
    }
    if ((active as any).customType === "button") {
      const rect = (active as fabric.Group).getObjects()[0] as fabric.Rect;
      rect.set({ rx: radius, ry: radius });
      active.setCoords();
      this.canvas.renderAll();
    }
  }

  changeShapeColor(event: any) {
    const active = this.canvas.getActiveObject();
    if (!active) return;
    const color = event.target.value;
    if (active.type !== "group") {
      active.set("fill", color);
      this.canvas.renderAll();
      return;
    }
    if ((active as any).customType === "button") {
      const rect = (active as fabric.Group).getObjects()[0] as fabric.Rect;
      rect.set("fill", color);
      this.canvas.renderAll();
    }
  }

  increaseIndent() {
    const obj = this.canvas.getActiveObject();
    if (obj && obj.type === "textbox") {
      (obj as fabric.Textbox).set("padding", ((obj as fabric.Textbox).padding || 0) + 10);
      obj.setCoords(); this.canvas.renderAll();
    }
  }

  decreaseIndent() {
    const obj = this.canvas.getActiveObject();
    if (obj && obj.type === "textbox") {
      (obj as fabric.Textbox).set("padding", Math.max(((obj as fabric.Textbox).padding || 0) - 10, 0));
      obj.setCoords(); this.canvas.renderAll();
    }
  }

  increaseLineSpacing() {
    const obj = this.canvas.getActiveObject();
    if (obj && obj.type === "textbox") {
      (obj as fabric.Textbox).set("lineHeight", Math.min(((obj as fabric.Textbox).lineHeight || 1.2) + 0.1, 5));
      obj.setCoords(); this.canvas.renderAll();
    }
  }

  decreaseLineSpacing() {
    const obj = this.canvas.getActiveObject();
    if (obj && obj.type === "textbox") {
      (obj as fabric.Textbox).set("lineHeight", Math.max(((obj as fabric.Textbox).lineHeight || 1.2) - 0.1, 0.5));
      obj.setCoords(); this.canvas.renderAll();
    }
  }

  updateChanges(isSynced: boolean) {
    this.step_change.emit({
      meta: this.getSafeJSON(),
      width: this.creativeWidth,
      height: this.creativeHeight,
      templateId: this.templateId,
      synced: isSynced,
    });
  }

  onSyncClick() { this.updateChanges(true); }

  getSafeJSON() {
    const json = this.canvas.toJSON();
    json.objects.forEach((obj: any, i: number) => {
      const live = this.canvas.getObjects()[i];
      if (obj.type === "image" && (live as any)._gifUrl) {
        obj.src = (live as any)._gifUrl;
        obj._gifUrl = (live as any)._gifUrl;
        obj.isAnimatedGif = true;
      }
      if (obj.type === "circle") {
        if (!obj.radius && obj.width) obj.radius = (obj.width / 2) * (obj.scaleX || 1);
        obj.scaleX = 1; obj.scaleY = 1;
        delete obj.width; delete obj.height;
      }
      if (obj.type === "rect") {
        obj.rx = live.rx || 0;
        obj.ry = live.ry || 0;
      }
      if (["textbox", "text", "i-text"].includes(obj.type)) {
        obj.fontSize = live.fontSize;
        obj.fontFamily = live.fontFamily;
        obj.lineHeight = live.lineHeight;
        obj.fontWeight = live.fontWeight;
        obj.fontStyle = live.fontStyle;
        obj.textAlign = live.textAlign;
        obj.underline = live.underline;
        obj.charSpacing = live.charSpacing;
        obj.fill = live.fill;
        obj.font_url = (live as any).font_url; // CHANGED: keep font file reference
      }
    });
    return json;
  }

  async loadCanvasFromJSON(meta: any, width: number, height: number) {
    if (!meta || !this.canvas) return;
    this.isCanvasRestoring = true;
    this.creativeWidth = width;
    this.creativeHeight = height;
    this.canvas.setWidth(width);
    this.canvas.setHeight(height);
    this.canvas.clear();

    try {
      await this.fontLoader.ensureFontsForMeta(meta, this.stepInput?.globalParams);
    } catch (err) {
      console.warn('[fonts] preload failed, continuing with fallback fonts', err);
    }

    this.canvas.loadFromJSON(meta, async () => {
      this.canvas.getObjects().forEach((o: any) => {
        if (['textbox', 'text', 'i-text'].includes(o.type)) {
          o.initDimensions();
          o.setCoords();
        }
      });
      this.canvas.renderAll();
      this.restrictMovementInsideCanvas();
      this.undoStack = [];
      this.redoStack = [];
      this.saveState();

      this.canvas.getObjects("image").forEach((element: any) => {
        if (element.backend_value_src) this.drawPlaceholderLabel(element);
      });

      const gifObjects = this.canvas.getObjects("image").filter((obj: any) => obj._gifUrl);
      for (const obj of gifObjects) {
        await this.reAnimateGif(obj as any, (obj as any)._gifUrl);
      }
      this.canvas.renderAll();

      this.isCanvasRestoring = false;
      this.updateChanges(true);
    });
  }

  onUploadFont(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) return;

    const allowedFormats = ['ttf', 'otf', 'woff', 'woff2'];
    const extension = file.name.split('.').pop()?.toLowerCase();
    if (!extension || !allowedFormats.includes(extension)) {
      this.toastr.error('Please choose a valid font file (.ttf, .otf, .woff, .woff2)');
      input.value = '';
      return;
    }

    const obj = this.canvas?.getActiveObject() as fabric.Textbox;
    if (!obj || obj.type !== 'textbox') {
      this.toastr.warning('Select a text box first, then choose the custom font again');
      input.value = '';
      return;
    }

    const desc = this.fontLoader.parseFontName(file.name);

    const reader = new FileReader();
    reader.onload = () => {
      const buffer = reader.result as ArrayBuffer;
      const font = new FontFace(desc.family, buffer, { weight: desc.weight, style: desc.style });
      font.load().then(() => {
        (document.fonts as unknown as { add(f: FontFace): void }).add(font);
        fabric.util.clearFabricFontCache(desc.family);

        obj.set({ fontFamily: desc.family, fontWeight: desc.weight, fontStyle: desc.style } as any);
        obj.initDimensions();
        obj.setCoords();
        this.canvas.requestRenderAll();

        this.toastr.success(`Font "${desc.family}" applied to text`);

        this.uploadFontUrl(file).then((fontUrl) => {
          obj.set('font_url', fontUrl);
          const name = file.name.replace(/\.[^/.]+$/, '').replace(/\s+/g, '_');
          this.mergeFontAssets([{ name, asset_url: fontUrl, asset_type: 'font' }]);
          this.selectedFont = name;
          this.canvas.fire('object:modified', { target: obj });
        }).catch(() => {
          this.toastr.error('Failed to upload font file');
        });
      }).catch(() => {
        this.toastr.error('Failed to load the font file');
      }).finally(() => {
        input.value = '';
      });
    };
    reader.onerror = () => {
      this.toastr.error('Failed to read the font file');
      input.value = '';
    };
    reader.readAsArrayBuffer(file);
  }

  uploadFontUrl(file: File): Promise<string> {
    const formData = new FormData();

    const sanitizedFontName = file.name.replace(/\s+/g, '_');
    formData.append("file", file);
    formData.append("asset_name", sanitizedFontName);
    formData.append("name", sanitizedFontName.split(".")[0]);
    formData.append("description", "");
    formData.append("asset_type", "font");
    formData.append("width", "100");
    formData.append("height", "100");
    formData.append("meta", JSON.stringify({}));

    return firstValueFrom(
      this.dcoLibraryService.create(formData, this.stepInput.globalParams)
    ).then((res: any) => res?.url ?? '');
  }

  onUploadTemplate(event: any) {
    const file = event.target.files?.[0];

    if (!file) return;

    const allowedFormats = ["jpg", "png", "jpeg", "svg", "gif"];
    const extension = file.name.split(".").pop()?.toLowerCase();

    if (!allowedFormats.includes(extension)) {
      this.toastr.error(`Invalid file format. Allowed: ${allowedFormats.join(", ")}`);
      event.target.value = "";
      return;
    }

    const img = new Image();
    img.src = URL.createObjectURL(file);
    img.onload = () => {
      this.creativeWidth = img.naturalWidth;
      this.creativeHeight = img.naturalHeight;
      let sizeMatched = false;

      if (this.creativeWidth && this.creativeHeight) {
        this.creativeSizes.forEach((size) => {
          const [width, height] = size.value.split("x").map(Number);
          if (this.creativeWidth === width && this.creativeHeight === height) {
            sizeMatched = true;
            const formData = new FormData();
            const sanitizedTemplateName = file.name.replace(/\s+/g, '_');
            formData.append("file", file);
            formData.append("asset_name", sanitizedTemplateName);
            formData.append("name", `${sanitizedTemplateName.split(".")[0]}_${Date.now()}`);
            formData.append("description", "");
            formData.append("asset_type", "template");
            formData.append("width", this.creativeWidth.toString());
            formData.append("height", this.creativeHeight.toString());
            formData.append("meta", JSON.stringify({}));
            formData.append("hidden", "true");

            this.dcoLibraryService.create(formData, this.stepInput.globalParams).subscribe((res) => {
              this.changeCreativeSize({ value: `${this.creativeWidth}x${this.creativeHeight}` });
              this.uploadImageFromUrl(
                { asset_url: res.url, asset_type: "template", asset_name: decodeURIComponent(file.name).replace(/\s+/g, '_'), mime_type: file.type, size_kb: file.size / 1024 },
                false,
                true
              );
            });
          }
        });

        if (!sizeMatched) {
          this.creativeWidth = 0;
          this.creativeHeight = 0;
          this.canvas.clear();
          this.canvas.renderAll();
          this.toastr.error("Invalid Creative Sizes");
          return;
        }
      }
    };
  }

  showPreview() {
    this.dialog.open(CreativePreviewComponent, {
      disableClose: false,
      panelClass: "actionpreview-modalbox",
      data: {
        meta: this.getSafeJSON(),
        width: this.creativeWidth,
        height: this.creativeHeight,
        component: this.stepInput.component,
        globalParams: this.stepInput.globalParams,
        product_feed_id: this.stepInput.product_feed_id,
      },
    });
  }
}